import type { Dictionary } from './fr';

/**
 * Dictionnaire ARABE (arabe standard moderne).
 *
 * ⚠️ Traduction automatique relue par IA (Claude, 17/10/2026), pas par un locuteur natif.
 *
 * Registre : arabe standard moderne simple, adresse à la 2e personne. Boutons et titres en noms
 * d'action, neutres en genre (« تحليل سيرتي الذاتية ») ; phrases et impératifs au masculin singulier
 * conventionnel (« أعد المحاولة »). Pluriels : `other` est toujours « libellé : %{count} » (ou
 * « libellé (%{count}) » dans les fragments), juste pour tout nombre ≥ 2. Nombres fixes : 1 et 2 en
 * toutes lettres (singulier / duel), 3 et plus en chiffres occidentaux.
 * Glossaire : alternance → « التكوين بالتناوب » (libellé de contrat : « تكوين بالتناوب » ; rythme : « إيقاع التناوب ») ; stage → « تدريب » ;
 * s'entraîner → « تمرّن » ; école → « المدرسة » ; CFA → sigle gardé : « مراكز تكوين المتمهّنين (CFA) » ;
 * CV → « السيرة الذاتية » ; lettre de motivation → « رسالة التحفيز » ; entretien → « مقابلة » ;
 * offre → « عرض » ; candidature → « طلب » ; fiche de poste → « الوصف الوظيفي » ; annonce → « إعلان » ;
 * recruteur → « مسؤول التوظيف » ; notation → « تقييم » ; score → « درجة » ; coaching → « التوجيه المهني » ;
 * formule → « باقة » ; plan → « خطة » ; créneau → « موعد » ; CDI / CDD → sigle toujours gardé, avec une
 * glose : « عقد دائم (CDI) » / « عقد محدد المدة (CDD) » (glose puis sigle, ordre de la relecture).
 * Plans Free / Plus / Pro gardés en latin ; ATS, marques et e-mails aussi.
 */
export const ar: Dictionary = {
  common: {
    back: 'رجوع',
    scoreSur100: 'من 100',
    cancel: 'إلغاء',
    save: 'حفظ',
    retry: 'إعادة المحاولة',
    delete: 'حذف',
    ok: 'فهمت',
  },

  a11y: {
    close: 'إغلاق',
    removeLanguage: 'إزالة اللغة',
    removeBullet: 'إزالة النقطة',
    removeProject: 'إزالة المشروع',
    removeEducation: 'إزالة التعليم',
    removeExperience: 'إزالة الخبرة',
    removeTag: 'إزالة %{tag}',
    useful: 'مفيد',
    notUseful: 'غير مفيد',
    listen: 'الاستماع إلى هذه الرسالة',
    notifications: 'الإشعارات',
    settings: 'الإعدادات',
    changePhoto: 'تغيير صورة الملف الشخصي',
    changeSearchTarget: 'تغيير هدف بحثك',
    importPhoto: 'استيراد صورة',
    score: 'النتيجة: %{score} من 100',
  },

  nav: {
    carriere: 'المسار المهني',
    offers: 'العروض',
    news: 'الأخبار',
    profile: 'الملف الشخصي',
    aiAction: 'إجراء بالذكاء الاصطناعي',
  },

  home: {
    content: {
      seeAll: 'عرض الكل',
      new: 'جديد',
      duration: '%{minutes} د',
      play: 'استماع',
      types: {
        podcast: 'بودكاست AlternJob',
        tip: 'نصيحة اليوم',
        event: 'لا تفوّته',
        success: 'لقد نجحوا',
        market: 'السوق والعروض',
        masterclass: 'ماستر كلاس',
        challenge: 'تحدٍّ',
        news: 'جديد في Horméia',
      },
      sample: {
        title: 'الذكاء الاصطناعي والتوظيف: ما الذي يتغير فعلًا في 2024',
        description: 'نصائح وشهادات واتجاهات سوق العمل.',
      },
    },
  },


  carriere: {
    title: 'المسار المهني',
    headline: 'كن المرشّح\nالذي يريد مسؤولو التوظيف',
    headlineAccent: 'الاتصال به.',
    position: 'البطاقة %{index} من %{total}',
    cv: {
      title: 'سيرة ذاتية ATS',
      description:
        'أنشئ سيرة ذاتية احترافية، محسّنة لتجاوز أنظمة الفرز ولفت انتباه مسؤولي التوظيف.',
      cta: 'تحليل سيرتي الذاتية',
    },
    linkedin: {
      title: 'LinkedIn',
      description:
        'أنشئ ملفًا احترافيًا وعزّز ظهورك لدى مسؤولي التوظيف والشركات.',
      cta: 'تحسين ملفي',
    },
    letter: {
      title: 'رسالة التحفيز',
      description: 'اكتب رسائل مؤثرة ومخصّصة لكل طلب توظيف.',
      cta: 'كتابة رسالتي',
    },
    interview: {
      title: 'مقابلة بالذكاء الاصطناعي',
      description: 'تمرّن وانجح في كل مقابلاتك.',
      cta: 'التمرّن الآن',
    },
    // Page Carrière sur grand écran : panneau « prochain pas », chiffres et état de chaque outil.
    tableau: {
      prochainPas: 'خطوتك التالية للحصول على مقابلات',
      premierPas: 'ابدأ بمعرفة موقعك',
      conseilParDefaut: 'يمكن لسيرتك الذاتية أن تتحسن أكثر: افتح التفاصيل لرؤية التصحيحات.',
      sansScore: 'ارفع سيرتك الذاتية: تحصل على درجة ATS وأهم التصحيحات في ثوانٍ.',
      corriger: 'تصحيح سيرتي الذاتية',
      noter: 'تقييم سيرتي الذاتية',
      chiffreCv: { one: 'سيرة ذاتية محفوظة', other: 'سير ذاتية محفوظة' },
      chiffreLettres: { one: 'رسالة مكتوبة', other: 'رسائل مكتوبة' },
      chiffreCorrections: { one: 'تصحيح مقترح', other: 'تصحيحات مقترحة' },
      statutCvAucun: 'لا توجد سيرة ذاتية بعد',
      statutCv: { one: 'سيرة ذاتية واحدة', other: '%{count} سير ذاتية' },
      statutScore: 'الدرجة %{score}',
      statutLettreAucune: 'لا توجد رسائل بعد',
      statutLettres: { one: 'رسالة واحدة مكتوبة', other: 'رسائل مكتوبة: %{count}' },
      statutLinkedin: 'تدقيق في دقيقة',
      statutInterview: 'مسؤول توظيف أو مدير',
    },
  },

  offers: {
    title: 'العروض',
    subtitle: 'قريبًا: عروض مختارة حسب ملفك وأبحاثك.',
    soonTitle: 'ميزة جديدة قيد الإنشاء',
    soonBody:
      'نُعدّ مجموعة مختارة من عروض التكوين بالتناوب وأول وظيفة تتوافق مع سيرتك الذاتية وما تبحث عنه. في انتظار ذلك، يمكنك العمل على سيرتك الذاتية وملفك على LinkedIn ورسائلك من تبويب المسار المهني.',
  },

  offres: {
    franceUniquement: 'تخصّ العروض وظائف في فرنسا فقط، وهي مكتوبة بالفرنسية.',
    titre: 'العروض',
    sousTitre: 'مختارة حسب معاييرك',
    credits: { zero: 'لم يتبقَّ أي طلب بالذكاء الاصطناعي هذا الشهر', one: 'يتبقّى لك طلب واحد بالذكاء الاصطناعي', other: 'الطلبات المتبقية بالذكاء الاصطناعي: %{count}' },
    segments: { pourToi: 'لك', sauvegardees: 'المحفوظة' },
    modifierCriteres: 'تعديل معاييري',
    sources: { france_travail: 'France Travail', lba: 'La bonne alternance', adzuna: 'Jobs by Adzuna' },
    contrats: { alternance: 'تكوين بالتناوب', stage: 'تدريب', cdi: 'عقد دائم (CDI)', cdd: 'عقد محدد المدة (CDD)', autre: 'عقد آخر' },
    contratsEnLigne: { alternance: 'تكوين بالتناوب', stage: 'تدريب', cdi: 'عقد دائم (CDI)', cdd: 'عقد محدد المدة (CDD)', autre: 'عقد آخر' },
    rythmes: {
      '3j2j': '3 أيام / يومان',
      '4j1j': '4 أيام / يوم',
      '1sem1sem': 'أسبوع / أسبوع',
      '2sem2sem': 'أسبوعان / أسبوعان',
      '3sem1sem': '3 أسابيع / أسبوع',
      '2j3j': 'يومان / 3 أيام',
    },
    rythmesComplets: {
      '3j2j': '3 أيام في الشركة، ويومان في المدرسة',
      '4j1j': '4 أيام في الشركة، ويوم واحد في المدرسة',
      '1sem1sem': 'أسبوع في الشركة، وأسبوع في المدرسة',
      '2sem2sem': 'أسبوعان في الشركة، وأسبوعان في المدرسة',
      '3sem1sem': '3 أسابيع في الشركة، وأسبوع في المدرسة',
    },
    dureesStage: { '2': 'شهران', '3': '3 أشهر', '4': '4 أشهر', '5': '5 أشهر', '6': '6 أشهر' },
    dureesCdd: { lt6: 'أقل من 6 أشهر', '6a12': 'من 6 إلى 12 شهرًا', '12plus': 'سنة فأكثر' },
    rayonKm: '%{km} كم',
    preferences: {
      titre: 'معاييرك',
      sousTitre: 'نبحث بحسب ما تختاره هنا.',
      contratMulti: 'يمكنك اختيار أكثر من واحد',
      peuImporte: 'لا يهم',
      cdiSeul: 'في العقد الدائم (CDI) لا يوجد شيء آخر للاختيار.',
      verifier: 'تحقّق من الحقول المشار إليها أعلاه.',
      metier: 'الوظيفة المستهدفة',
      metierPlaceholder: 'مثال: مسؤول تواصل رقمي',
      contrat: 'نوع العقد',
      ville: 'المدينة',
      villePlaceholder: 'مثال: Lyon',
      rayon: 'نطاق البحث',
      rythme: 'إيقاع التناوب',
      rythmeAide: 'الشركة / المدرسة',
      dureeStage: 'مدة التدريب',
      dureeCdd: 'مدة العقد المحدد المدة',
      dureeAide: 'تُستبعد العروض التي تُعلن مدة مختلفة.',
      reponsesTitre: 'إجابات أساسية، لا تُختلق أبدًا',
      permis: 'رخصة القيادة (B)',
      oui: 'نعم',
      non: 'لا',
      aRenseigner: 'لم يُحدَّد بعد',
      disponibilite: 'التوفر ابتداءً من',
      disponibilitePlaceholder: 'DD/MM/YYYY',
      voir: 'عرض عروضي',
      metierRequis: 'حدّد الوظيفة التي تستهدفها.',
      villeRequise: 'حدّد مدينتك.',
      villeIntrouvable: 'لم نعثر على هذه المدينة في فرنسا. تحقّق من الإملاء أو اختر مدينة فرنسية.',
      villeRetenue: 'المدينة المفهومة: %{commune}. إن لم تكن الصحيحة فصحّحها، وإلا فأكّد مرة أخرى.',
      contratRequis: 'اختر نوع عقد واحدًا على الأقل.',
      disponibiliteInvalide: 'اكتب التاريخ بصيغة DD/MM/YYYY.',
      erreurEnregistrement: 'تعذّر حفظ معاييرك. أعد المحاولة.',
      chargement: 'جارٍ تحميل معاييرك…',
      retour: 'العودة إلى العروض',
    },
    raisons: {
      contrat: 'العقد: %{valeur}',
      contrat_different: 'عقد مختلف عمّا تبحث عنه',
      distance: 'على بُعد %{valeur} كم من مدينتك',
      distance_loin: 'على بُعد %{valeur} كم، خارج نطاقك',
      distance_inconnue: 'مكان العمل غير محدّد',
      distance_rayon: 'ضمن نطاق %{valeur} كم',
      rythme: 'إيقاع %{valeur}، كما تريد',
      rythme_different: 'إيقاع %{valeur}، مختلف عن إيقاعك',
      rythme_absent: 'إيقاع التناوب غير محدّد',
      metier: 'مسمّى قريب من وظيفتك: %{valeur}',
      metier_partiel: 'مسمّى قريب جزئيًا: %{valeur}',
      metier_rome: 'من عائلة الوظائف نفسها (رمز ROME %{valeur})',
      metier_eloigne: 'مسمّى بعيد عن الوظيفة التي تستهدفها',
      metier_sans_citation: 'يطابق الوظيفة التي تستهدفها',
      metier_partiel_sans_citation: 'قريب من الوظيفة التي تستهدفها',
      permis_a_renseigner: 'رخصة القيادة (B) مطلوبة: أكمل معلوماتك',
      permis_manquant: 'رخصة القيادة (B) مطلوبة',
    },
    carte: {
      pourquoi: 'لماذا يناسبك',
      publiee: {
        one: 'نُشر على %{source} قبل يوم واحد',
        two: 'نُشر على %{source} قبل يومين',
        few: 'نُشر على %{source} قبل %{jours} أيام',
        many: 'نُشر على %{source} قبل %{jours} يومًا',
        other: 'نُشر على %{source} قبل %{jours} يوم',
      },
      publieeAujourdhui: 'نُشر اليوم على %{source}',
      voirDetail: 'عرض التفاصيل',
      voirOffreComplete: 'عرض الإعلان كاملًا',
      contratDuree: '%{contrat} · %{duree}',
      dureeMois: {
        one: 'شهر واحد',
        two: 'شهران',
        few: '%{count} أشهر',
        many: '%{count} شهرًا',
        other: '%{count} شهر',
      },
      salaire: 'الراتب: %{valeur}',
      entrepriseInconnue: 'الشركة غير مُعلنة',
      lieuDistance: '%{km} كم · %{lieu}',
      score: 'درجة التوافق: %{score} من 100',
    },
    actions: {
      passer: 'تخطّي هذا العرض',
      sauvegarder: 'حفظ هذا العرض',
      interesse: 'هذا العرض يهمّني',
      aide: 'اسحب يمينًا إن كان العرض يهمّك، ويسارًا إن لم يكن',
      libelles: { passer: 'تخطّي', sauvegarder: 'حفظ', interesse: 'يهمّني' },
    },
    detail: {
      voirCandidature: 'عرض طلبي',
      alerteMineur: 'للتحقق: يبدو أن هذا العرض يتضمن %{motifs}، وهو ممنوع أو مقيّد لمن هم دون 18 عامًا.',
      motifsMineur: { nuit: 'العمل الليلي', alcool: 'تقديم الكحول', hauteur: 'العمل على ارتفاع' },
      fermer: 'إغلاق التفاصيل',
      monMatch: 'توافقك',
      description: 'الوصف',
      extrait: 'مقتطف من الإعلان',
      extraitAide: 'لا ترسل Adzuna سوى بداية الإعلان. النص الكامل متاح على موقعها.',
      voirOffreComplete: 'عرض الإعلان كاملًا',
      entreprise: 'الشركة',
      infos: 'معلومات',
      voirOffre: 'عرض الإعلان الأصلي',
      preparer: 'تحضير طلبي',
      source: 'المصدر: %{source}',
      majLe: 'آخر تحديث في %{date}',
      licence: 'إعادة الاستخدام خاضعة لترخيص France Travail',
      retiree: 'سحب مسؤول التوظيف هذا العرض.',
      logo: 'شعار %{entreprise}',
      sauvegardee: 'تم حفظ العرض',
      rubriques: {
        nature_contrat: 'طبيعة العقد',
        type_contrat: 'نوع العقد',
        duree_travail: 'مدة العمل',
        experience: 'الخبرة',
        formations: 'التعليم',
        competences: 'المهارات',
        qualites: 'الصفات المهنية',
        savoirs: 'الأدوات',
        langues: 'اللغات',
        permis: 'الرخص',
        salaire: 'الراتب',
        secteur: 'قطاع النشاط',
        qualification: 'التأهيل',
        deplacements: 'التنقلات',
        conditions: 'ظروف العمل',
        postes: 'عدد المناصب',
        accessible_th: 'متاح للعمال ذوي الإعاقة',
        contact: 'جهة الاتصال',
        agence: 'وكالة France Travail',
        duree_contrat: 'مدة العقد',
        debut: 'بداية العقد',
        expiration: 'العرض صالح حتى',
        rome: 'رمز ROME',
      },
    },
    preparation: {
      parallele: 'بالتوازي',
      motsClesTitre: 'الكلمات المفتاحية المرصودة في العرض',
      motsClesAutres: '+%{count}',
      motsClesA11y: { one: 'كلمة مفتاحية واحدة مرصودة: %{liste}', other: 'الكلمات المفتاحية المرصودة (%{count}): %{liste}' },
      motsClesCouvertsA11y: 'الكلمات المفتاحية التي تغطيها سيرتك الذاتية (%{couverts} من %{total}): %{liste}',
      titre: 'ذكاؤك الاصطناعي يحضّر طلبك',
      pret: 'طلبك جاهز',
      pour: 'لدى %{entreprise} · %{poste}',
      pourPoste: 'لوظيفة %{poste}',
      fermer: 'إغلاق التحضير',
      terminer: 'إغلاق',
      verifier: 'مراجعة طلبي',
      reessayer: 'إعادة المحاولة',
      retourOffres: 'العودة إلى العروض',
      pied: 'لا يُرسل شيء دون موافقتك',
      progression: 'تحضير الطلب',
      etapes: { analyse: 'تحليل العرض', cv: 'سيرة ذاتية مكيّفة مع العرض', lettre: 'رسالة التحفيز', controle: 'مراقبة الجودة' },
      attente: 'قيد الانتظار',
      enCours: 'جارٍ…',
      echecEtape: 'فشلت هذه الخطوة',
      motsCles: { zero: 'لا كلمات مفتاحية', one: 'كلمة مفتاحية واحدة', other: 'كلمات مفتاحية (%{count})' },
      questions: { zero: 'لا أسئلة', one: 'سؤال واحد', other: 'أسئلة (%{count})' },
      analyseFaite: 'رُصد: %{motsCles}، %{questions}',
      cvFait: { zero: 'لم تُدمج أي كلمة مفتاحية', one: 'دُمجت كلمة مفتاحية واحدة', other: 'الكلمات المفتاحية المدمجة: %{count}' },
      titreAligne: '، مع عنوان مطابق للعرض',
      lettreFaite: 'عدد كلمات الرسالة: %{count}',
      controleFait: { zero: 'لا توجد نقاط للتأكيد', one: 'نقطة واحدة للتأكيد', other: 'نقاط للتأكيد: %{count}' },
      couverture: 'الكلمات المفتاحية المغطّاة: %{couverts} من %{total}',
      erreurs: {
        quota: 'استخدمت كل طلباتك بالذكاء الاصطناعي هذا الشهر.',
        cv_manquant: 'تحتاج إلى سيرة ذاتية رئيسية لتحضير طلب. أنشئ سيرتك الذاتية أو استوردها في المسار المهني.',
        offre_retiree: 'هذا العرض لم يعد متاحًا: لا يمكن تحضير الطلب.',
        profil_trop_eloigne: 'هذا العرض بعيد جدًا عن سيرتك الذاتية لكتابة رسالة مقنعة.',
        echec: 'فشلت إحدى الخطوات. أعد المحاولة: الخطوات المنجزة محفوظة.',
      },
    },
    avant: {
      fermer: 'إغلاق',
      naissanceTitre: 'تاريخ ميلادك',
      naissanceRaison: 'يُستخدم لتكييف نبرة رسالتك ولتنبيهك إن كان عرض ما غير مسموح به قبل سن 18. لن تُدخله إلا مرة واحدة.',
      naissanceFormat: 'DD/MM/YYYY',
      erreurNaissance: 'تعذّر حفظ تاريخ ميلادك. أعد المحاولة.',
      continuer: 'متابعة',
      annuler: 'إلغاء',
      eloigneeTitre: 'هذا العرض بعيد عن ملفك',
      eloigneeTexte: 'درجة توافقك %{score} من 100. يمكنك تحضير طلبك رغم ذلك: سيُخصم رصيد واحد إذا اكتمل.',
      prepareQuandMeme: 'التحضير رغم ذلك',
      nePasPreparer: 'عدم التحضير',
    },
    relecture: {
      titre: 'راجع طلبك',
      fermer: 'إغلاق المراجعة',
      erreurLecture: 'تعذّر تحميل طلبك.',
      reessayer: 'إعادة المحاولة',
      manquants: 'غير موجودة في سيرتك الذاتية: %{liste}',
      aConfirmer: 'للتحقق قبل الإرسال',
      raisons: {
        nombre: 'رقم غير موجود في سيرتك الذاتية',
        date: 'تاريخ غير موجود في سيرتك الذاتية',
        competence: 'مهارة لا تُظهرها سيرتك الذاتية',
        a_confirmer: 'للتأكيد',
      },
      exact: 'نعم، هذا صحيح',
      corriger: 'تصحيح',
      enregistrer: 'حفظ',
      annuler: 'إلغاء',
      champCorrection: 'تصحيحك',
      confirme: 'تم التأكيد',
      corrige: 'تم التصحيح',
      erreurCorrection: 'تعذّر حفظ هذا التصحيح. أعد المحاولة.',
      onglets: { cv: 'السيرة', lettre: 'الرسالة', reponses: 'الإجابات' },
      legende: 'المُظلَّل: عدّله الذكاء الاصطناعي لهذا العرض.',
      formation: 'التعليم',
      competences: 'المهارات',
      langues: 'اللغات',
      objet: 'الموضوع:',
      copierLettre: 'نسخ الرسالة',
      erreurCopie: 'تعذّر فتح المشاركة. أعد المحاولة.',
      reponses: {
        oui: 'نعم',
        non: 'لا',
        date: 'التوفر ابتداءً من %{date}',
        age: '%{age} سنة',
        aRenseigner: 'لم يُحدَّد بعد',
        aucune: 'لم تُرصد أي أسئلة إقصائية في هذا العرض.',
      },
      exporterCv: 'تصدير السيرة الذاتية (PDF)',
      exporterLettre: 'تصدير الرسالة (PDF)',
      erreurExport: 'فشل التصدير. أعد المحاولة.',
      voirOffre: 'عرض الإعلان',
      voirOffreComplete: 'عرض الإعلان كاملًا',
      extrait: 'حُضّر انطلاقًا من مقتطف من الإعلان: اقرأ الإعلان كاملًا قبل إرسال طلبك.',
    },
    etats: {
      chargement: 'نبحث عن عروضك…',
      erreurTitre: 'تعذّر تحميل العروض',
      erreurTexte: 'تحقق من اتصالك، ثم أعد المحاولة.',
      reessayer: 'إعادة المحاولة',
      videTitre: 'لا توجد عروض لهذه المعايير',
      videTexte: 'وسّع نطاق البحث أو جرّب وظيفة قريبة.',
      videTexteDuree: 'وسّع نطاق البحث أو جرّب وظيفة قريبة أو خفّف شرط مدة العقد.',
      epuiseTitre: 'شاهدت كل العروض المتاحة حاليًا',
      epuiseTexte: 'عُد لاحقًا، أو وسّع معاييرك لرؤية المزيد.',
      sourcePartielle: 'أحد مصادر العروض لا يستجيب: قد تكون القائمة ناقصة.',
      sauvegardeesVideTitre: 'لا توجد عروض محفوظة',
      sauvegardeesVideTexte: 'المس إشارة الحفظ على عرض لتجده هنا.',
      retiree: 'عرض مسحوب',
      decisionEchec: 'تعذّر حفظ اختيارك. تحقق من اتصالك.',
    },
  },

  news: {
    title: 'الأخبار',
    segments: { forYou: 'لك', favorites: 'المفضلة' },
    verified: 'ناشر موثّق',
    readingTime: 'مدة القراءة: %{count} د',
    relative: {
      today: 'اليوم',
      yesterday: 'أمس',
      days: {
        one: 'منذ يوم',
        two: 'منذ يومين',
        few: 'منذ %{count} أيام',
        many: 'منذ %{count} يومًا',
        other: 'منذ %{count} يوم',
      },
      weeks: {
        one: 'منذ أسبوع',
        two: 'منذ أسبوعين',
        few: 'منذ %{count} أسابيع',
        many: 'منذ %{count} أسبوعًا',
        other: 'منذ %{count} أسبوع',
      },
      months: {
        one: 'منذ شهر',
        two: 'منذ شهرين',
        few: 'منذ %{count} أشهر',
        many: 'منذ %{count} شهرًا',
        other: 'منذ %{count} شهر',
      },
      years: {
        one: 'منذ سنة',
        two: 'منذ سنتين',
        few: 'منذ %{count} سنوات',
        many: 'منذ %{count} سنة',
        other: 'منذ %{count} سنة',
      },
    },
    like: {
      add: 'الإعجاب بهذا المقال',
      remove: 'إلغاء الإعجاب',
      count: {
        zero: 'لا إعجابات',
        one: 'إعجاب واحد',
        other: 'الإعجابات: %{count}',
      },
      failed: 'تعذّر تحديث هذا الإعجاب.',
    },
    favorite: {
      add: 'إضافة إلى المفضلة',
      remove: 'إزالة من المفضلة',
      added: 'في المفضلة',
      notAdded: 'ليس في المفضلة',
      failed: 'تعذّر تحديث المفضلة.',
    },
    errorTitle: 'تعذّر تحميل الموجز',
    errorBody: 'تحقق من اتصالك، ثم أعد المحاولة.',
    emptyTitle: 'لا توجد مقالات حاليًا',
    emptyBody: 'ستظهر المقالات الجديدة هنا فور نشرها.',
    favoritesEmptyTitle: 'لا توجد مفضلات',
    favoritesEmptyBody: 'المس إشارة الحفظ على مقال لتجده هنا.',
    favoritesSignedOutTitle: 'سجّل الدخول للاحتفاظ بمقالاتك',
    favoritesSignedOutBody: 'مفضلاتك مرتبطة بحسابك وتتبعك من جهاز إلى آخر.',
    end: 'أنت مطّلع على كل جديد.',
  },

  notifications: {
    welcome: {
      title: 'مرحبًا بك في Horméia 👋',
      body: 'ابدأ بإنشاء سيرة ذاتية محسّنة لأنظمة ATS، ثم تمرّن على المقابلة مع الذكاء الاصطناعي. خطوتان، وتحصل على مقابلات أكثر.',
    },
    quantify: {
      title: 'عبّر عن نتائجك بالأرقام',
      body: 'في سيرتك الذاتية، استبدل «إدارة شبكات التواصل الاجتماعي» بـ«+35% تفاعل خلال 3 أشهر». مسؤولو التوظيف وأنظمة ATS يتذكرون الأرقام.',
    },
    followUp: {
      title: 'المتابعة بعد 7 أيام',
      body: 'إن لم تتلقَّ ردًا بعد أسبوع من تقديم طلبك، أرسل رسالة قصيرة ومهذبة إلى مسؤول التوظيف: المتابعة الجيدة غالبًا ما تضاعف فرص الرد.',
    },
  },

  myCv: {
    title: 'سيرتي الذاتية',
    scoreLabel: 'درجة ATS الخاصة بك',
    scoreNone: 'ابدأ أول تقييم لاكتشاف درجة ATS الخاصة بك.',
    scoreClean: 'لا توجد تحسينات معلّقة. سيرتك الذاتية جاهزة.',
    scoreAdvice: {
      one: 'لا يزال بإمكان تحسين واحد أن يزيد فرصك.',
      other: 'تحسينات متبقية: %{count}. يمكنها أن تزيد فرصك.',
    },
    scoreActionFirst: 'تقييم سيرتي الذاتية',
    scoreActionDetail: 'عرض التفاصيل',
    createOrImport: 'إنشاء أو استيراد',
    importTitle: 'استيراد سيرتي الذاتية',
    importSubtitle: 'PDF أو Word',
    createTitle: 'إنشاء سيرة ذاتية',
    createSubtitle: 'انطلاقًا من قالب',
    improve: 'تحسين سيرتي الذاتية',
    scoreRowTitle: 'قيّم سيرتي الذاتية',
    scoreRowSubtitle: 'درجة ATS والأولويات',
    optimiseTitle: 'حسّن سيرتي الذاتية',
    optimiseSubtitle: 'تحسين السيرة الذاتية الحالية',
    myCvs: 'سيري الذاتية',
    savedCount: {
      zero: 'لا توجد سيرة ذاتية محفوظة',
      one: 'سيرة ذاتية واحدة محفوظة',
      other: 'السير الذاتية المحفوظة: %{count}',
    },
    savedListOpen: 'عرض سيري الذاتية المحفوظة',
    listSubtitle: 'اعثر على سيرك الذاتية وأعد فتحها وأدِرها',
    priorityLabel: 'صحّح هذا أولًا',
    importShort: 'استيراد',
    createShort: 'إنشاء سيرة ذاتية',
    cvNote: 'السيرة الذاتية المُقيَّمة',
    changer: 'تغيير',
  },

  cvList: {
    title: 'سيري الذاتية',
    subtitle: 'اعثر على سيرك الذاتية المحفوظة وأعد فتحها وأدِرها.',
    loadError: 'تعذّر تحميل سيرك الذاتية. أعد المحاولة.',
    emptyTitle: 'لا توجد سيرة ذاتية حاليًا.',
    emptyText: 'أنشئ أول سيرة ذاتية محسّنة لأنظمة ATS في بضع دقائق.',
    emptyCta: 'إنشاء سيرتي الذاتية',
    primary: 'رئيسية',
    setPrimary: 'تعيينها رئيسية',
    edit: 'تحرير',
    preview: 'معاينة',
    others: 'سيري الذاتية الأخرى',
    score: 'درجة ATS %{score}',
    more: 'المزيد من الإجراءات',
    deleteTitle: 'حذف هذه السيرة الذاتية؟',
    deleteBody: 'سيتم حذف %{title} نهائيًا.',
  },

  cvImprove: {
    title: 'حسّن سيرتي الذاتية',
    subtitle: 'يقترح الذكاء الاصطناعي تعديلات محددة، وتحتفظ بما تريده منها.',
    loading: 'الذكاء الاصطناعي يحلّل سيرتك الذاتية…',
    blocked: 'هذا المسار مخصّص للمشتركين.',
    notFound: 'السيرة الذاتية غير موجودة. عُد إلى التقييم وأعد المحاولة.',
    saveFailed: 'تعذّر الحفظ.',
    reponseInvalide: 'الرد غير مكتمل. أعد المحاولة بعد قليل.',
    defaultTitle: 'سيرة ذاتية محسّنة',
    choixQuestion: 'هل تريد استيراد وصف وظيفي أو إعلان لتحسين توافق سيرتك الذاتية مع العرض؟',
    choixDeja: 'قيّمت سيرتك الذاتية باستخدام وصف وظيفي. هل تريد استخدامه لتحسينها؟',
    choixNon: 'لا، التحسين مباشرة',
    choixOui: 'نعم، إضافة وصف وظيفي',
    choixUtiliser: 'استخدام الوصف الوظيفي المستورد',
    ficheTitre: 'الوصف الوظيفي',
    ficheSousTitre: 'ستُكيَّف سيرتك الذاتية مع هذا العرض، دون اختلاق أي شيء.',
    ficheChamp: 'لقطات شاشة أو PDF أو نص',
    ficheAide: 'يمكنك الجمع بينها: ستُقرأ كعرض واحد. 5 لقطات شاشة و8 ميغابايت كحد أقصى، وملف PDF من 20 صفحة كحد أقصى.',
    optimiser: 'تحسين سيرتي الذاتية',
    passer: 'تخطّي',
    ficheUtilisee: 'مكيّفة مع الوصف الوظيفي المقدَّم.',
    // Optimisation par modifications (1.2.0).
    revue: {
      titre: {
        one: 'تعديل واحد مقترح',
        two: 'تعديلان مقترحان',
        few: '%{count} تعديلات مقترحة',
        many: '%{count} تعديلًا مقترحًا',
        other: '%{count} تعديل مقترح',
      },
      aide: 'اقبل كل تعديل أو ارفضه. لا يتغيّر شيء في سيرتك الذاتية قبل أن تطبّق.',
      aideTitre: 'اقبل كل تعديل أو ارفضه.',
      aideSuite: 'لا يتغيّر شيء في سيرتك الذاتية قبل أن تطبّق.',
      // Libellé SEUL, posé à côté du nombre affiché dans sa tuile (le titre complet reste `titre`).
      libelle: { one: 'تعديل مقترح', two: 'تعديلان مقترحان', few: 'تعديلات مقترحة', many: 'تعديلًا مقترحًا', other: 'تعديل مقترح' },
      score: 'لا يُعاد تقييم السيرة الذاتية المعدَّلة: قيّمها بعد ذلك لمعرفة درجة ATS.',
    },
    tags: { impact: 'الأثر', action: 'فعل الإنجاز', keywords: 'الكلمات المفتاحية', clarity: 'الوضوح', title: 'العنوان' },
    modif: { avant: 'قبل', apres: 'بعد', appliquer: 'تطبيق: %{nom}', titreCv: 'عنوان السيرة الذاتية' },
    pied: {
      retenues: 'المحتفَظ بها: %{count} من %{total}',
      appliquer: {
        one: 'تطبيق تعديل واحد',
        two: 'تطبيق التعديلين',
        few: 'تطبيق %{count} تعديلات',
        many: 'تطبيق %{count} تعديلًا',
        other: 'تطبيق %{count} تعديل',
      },
      aucune: 'لم تُختر أي تعديلات',
    },
    vide: { titre: 'سيرتك الذاتية قوية بالفعل', detail: 'لم يجد الذكاء الاصطناعي أي تعديل مفيد يقترحه. لم يُخصم شيء من حصتك.' },
    scoreInconnu: { titre: 'الدرجة غير مقاسة', detail: 'قيّم هذه السيرة الذاتية لمعرفة درجة ATS.' },
  },

  ficheDePoste: {
    choisirOffre: 'اختيار عرض',
    chargement: 'جارٍ تحميل عروضك…',
    erreur: 'تعذّر تحميل عروضك المحفوظة.',
    vide: 'لا توجد عروض محفوظة. احفظ عرضًا في تبويب العروض لتجده هنا.',
    coller: 'لصق الإعلان كاملًا',
    compteur: 'عدد الأحرف: %{n} / %{max}',
    retirerCapture: 'إزالة اللقطة %{n}',
    retirerFichier: 'إزالة الملف',
    erreurs: {
      tropDeCaptures: 'الحد الأقصى للقطات الشاشة: %{max}.',
      captureIllisible: 'تعذّرت قراءة إحدى اللقطات. اختر صورة أخرى.',
      tropLourd: 'تتجاوز اللقطات 8 ميغابايت إجمالًا. أزل واحدة للمتابعة.',
      pdfTropLong: 'يتجاوز ملف PDF هذا 20 صفحة. احتفظ بالوصف الوظيفي فقط.',
    },
  },

  cvScore: {
    failed: 'فشل التقييم. حاول مرة أخرى.',
    title: 'قيّم سيرتك الذاتية',
    targetRoleLabel: 'الوظيفة المستهدفة',
    targetRoleHint: 'الوظيفة التي تستهدفها: يقيّم الذكاء الاصطناعي أيضًا مدى ملاءمة سيرتك الذاتية لها.',
    targetRolePlaceholder: 'مثال: مطوّر ويب (تكوين بالتناوب)',
    offerLabel: 'الوصف الوظيفي',
    offerHint: 'أضف العرض للحصول على درجة توافق ونصائح موجّهة لهذه الوظيفة.',
    offerPlaceholder: 'الصق العرض هنا…',
    offerFile: 'العرض (PDF/Word)',
    offerCaptures: 'لقطات الشاشة',
    optional: 'اختياري',
    ficheAdded: 'تمت الإضافة',
    moreAccurate: 'لتقييم أدق',
    frise: { import: 'استيراد', evalue: 'تقييم الذكاء الاصطناعي', score: 'نتيجتك' },
    dropTitle: 'أضف سيرتك الذاتية',
    dropSub: 'PDF أو Word. تحصل على درجة ATS ونصائح عملية.',
    dropReady: 'سيرتك الذاتية جاهزة للتقييم',
    dropTextReady: 'نص سيرتك الذاتية جاهز.',
    dropPick: 'اختيار ملف',
    dropChange: 'تغيير الملف',
    hintEmpty: 'استورد سيرة ذاتية للحصول على درجة ATS.',
    hintReady: 'جاهز: تصلك النتيجة خلال ثوانٍ.',
    submit: 'تقييم سيرتي الذاتية',
    atsScore: 'درجة ATS',
    offerMatch: 'التوافق مع العرض',
    strengths: 'نقاط القوة لهذه الوظيفة',
    gaps: 'الفجوات مقارنة بالعرض',
    saveImported: 'حفظ في سيري الذاتية',
    saveFailed: 'تعذّر الحفظ. أعد المحاولة.',
    importedTitle: 'سيرة ذاتية مستوردة',
    improve: 'حسّن سيرتي الذاتية',
    verdict: { faible: 'يحتاج إلى تحسين', correct: 'مقبول، ويمكن أن يكون أفضل', bon: 'سيرة ذاتية جيدة، مع بعض التعديلات', excellent: 'ممتازة، جاهزة للإرسال' },
    resultDetail: {
      zero: 'لا توجد تحسينات معلّقة.',
      one: 'لا يزال بإمكان تحسين واحد أن يزيد فرصك.',
      other: 'تحسينات متبقية: %{count}. يمكنها أن تزيد فرصك.',
    },
    advicePrio: 'صحّح هذا أولًا',
    rescore: 'إعادة التقييم',
    rescoreTitre: 'إعادة التقييم؟',
    rescoreMessage: 'ستعود إلى النموذج وستُفقد النتيجة المعروضة.',
    rescoreAnnuler: 'الاحتفاظ بالنتيجة',
    criteres: { titre: 'التفاصيل حسب المعيار', keywords: 'الكلمات المفتاحية', layout: 'تنسيق متوافق مع ATS', impact: 'نتائج مقيسة بالأرقام', readability: 'سهولة القراءة' },
  },

  cvCreate: {
    questions: {
      role: { label: 'الوظيفة المستهدفة', placeholder: 'مثال: مسؤول مشروع' },
      education: {
        label: 'التعليم والشهادات',
        placeholder: 'مثال: M1 تسويق، IAE Paris',
      },
      experiences: {
        label: 'الخبرات المهنية (تدريب، تكوين بالتناوب، وظائف)',
        placeholder: 'صِف خبراتك المهنية',
      },
      projects: {
        label: 'المشاريع الشخصية (شخصية، جمعوية، جانبية)',
        placeholder: 'صِف مشاريعك الشخصية',
      },
      skills: { label: 'المهارات والأدوات', placeholder: 'مثال: SQL، SEO، Excel، Figma' },
      languages: { label: 'اللغات' },
      interests: {
        label: 'الاهتمامات والهوايات',
        placeholder: 'مثال: الجري في المسارات، التصوير، الشطرنج',
      },
      extra: {
        label: 'هل لديك ما تضيفه؟ (اختياري)',
        placeholder: 'حقل حر، وسيُدرجه الذكاء الاصطناعي في المكان المناسب',
      },
    },
    steps: {
      analyse: 'تحليل إجاباتك',
      format: 'تنسيق السيرة الذاتية',
      ats: 'التحقق من توافق ATS',
    },
  },

  languagePicker: {
    namePlaceholder: 'اللغة',
    level: 'المستوى',
    diploma: 'الشهادة (اختياري)',
    diplomaPlaceholder: 'مثال: TOEFL 105 (أو اتركه فارغًا)',
    add: 'إضافة لغة',
  },

  editors: {
    experience: {
      titre: 'الخبرة',
      rowTitle: 'الخبرة %{n}',
      roleLabel: 'المسمى الوظيفي',
      end: 'النهاية',
      descriptionLabel: 'ما قمت به',
      descriptionPlaceholder: 'اكتب ببساطة، سيعيد الذكاء الاصطناعي صياغته.',
      role: 'المسمّى الوظيفي',
      company: 'الشركة',
      start: 'البداية',
      current: 'الوظيفة الحالية',
      description: 'ما قمت به (سيعيد الذكاء الاصطناعي صياغته)',
      add: 'إضافة خبرة',
    },
    education: {
      titre: 'التعليم',
      rowTitle: 'التعليم %{n}',
      degree: 'الشهادة / المسمّى',
      school: 'المؤسسة',
      date: 'التاريخ',
      current: 'قيد الدراسة',
      add: 'إضافة مؤهل دراسي',
    },
    project: {
      titre: 'المشاريع',
      rowTitle: 'المشروع %{n}',
      descriptionLabel: 'ما أنجزته',
      descriptionPlaceholder: 'اكتب ببساطة، سيعيد الذكاء الاصطناعي صياغته.',
      name: 'اسم المشروع',
      date: 'التاريخ',
      current: 'قيد الإنجاز',
      description: 'ما أنجزته (سيعيد الذكاء الاصطناعي صياغته)',
      add: 'إضافة مشروع',
    },
    bullets: {
      placeholder: 'صِف نتيجة أو إجراءً',
      add: 'إضافة نقطة',
    },
    template: {
      groupLabel: 'قالب السيرة الذاتية',
      mono: 'احترافي',
      accent: 'ملوّن',
      accentColor: 'لون التمييز',
      couleur: {
        lime: 'ليموني',
        vert: 'أخضر',
        bleu: 'أزرق',
        beige: 'بيج',
        marron: 'بني',
        ardoise: 'رمادي أردوازي',
        marine: 'كحلي',
      },
    },
    quantifyHint: 'فكّر في التعبير عن نتائجك بالأرقام (كم، أي نسبة %، أي أثر). هذا يرفع درجة ATS الخاصة بك.',
    searchTarget: 'تبحث عن…',
  },

  cvEditor: {
    before: 'قبل',
    after: 'بعد - مقترح من الذكاء الاصطناعي',
    reject: 'رفض',
    accept: 'قبول',
    improveWithAi: 'التحسين بالذكاء الاصطناعي',
    headline: 'عنوان السيرة الذاتية',
    experiences: 'الخبرات',
    projects: 'المشاريع',
    education: 'التعليم',
    skills: 'المهارات',
    languages: 'اللغات',
    interests: 'الاهتمامات',
    headlinePlaceholder: 'مثال: سفير الذكاء الاصطناعي ومنسّق المجتمع',
    startPlaceholder: 'مثال: يناير 2024',
    endPlaceholder: 'مثال: حتى الآن',
    projectDatePlaceholder: 'مثال: مارس 2024',
    educationDatePlaceholder: 'مثال: سبتمبر 2025',
    skillPlaceholder: 'مثال: SQL',
    languagePlaceholder: 'مثال: الإنجليزية - C1',
    interestPlaceholder: 'مثال: الجري في المسارات',
    experienceLabel: 'خبرة',
    projectLabel: 'مشروع',
    startLabel: 'البداية',
    endLabel: 'النهاية',
    dateLabel: 'التاريخ',
    rewriteRetry: 'حاول مرة أخرى.',
  },

  qualityCheck: {
    title: 'ما تم تحسينه',
    corrections: 'التصحيحات',
    languagesRemoved: 'لغات أُزيلت (مستوى أقل من B2)',
    skillsNotProven: 'مهارات من العرض غير مُثبتة',
    toValidate: 'للتحقق من جانبك',
    removed: 'عناصر محذوفة',
    recommendations: 'توصيات',
  },

  linkedin: {
    sousScores: { titre: 'التفصيل حسب القسم', title: 'العنوان', about: 'نبذة', experiences: 'الخبرات' },
    kit: {
      failed: 'تعذّر إنشاء حزمة التحسين. درجتك وأولوياتك محفوظة.',
      retry: 'إعادة محاولة الحزمة',
    },
    title: 'تدقيق LinkedIn',
    subtitle: 'يقرأ الذكاء الاصطناعي ملفك ويخبرك بما يجب تحسينه.',
    verdict: { faible: 'الملف يحتاج إلى تعزيز', correct: 'ملف مقبول، ويمكن أن يكون أفضل', bon: 'ملف جيد، مع بعض التعديلات', excellent: 'ملف ممتاز' },
    resultDetail: {
      zero: 'لا توجد إجراءات ذات أولوية.',
      one: 'إجراء واحد ذو أولوية للتحسين.',
      other: '%{count} إجراءات ذات أولوية للتحسين.',
    },
    profilAnalyse: 'تم تحليل الملف',
    relancer: 'إعادة',
    relancerTitre: 'إعادة التدقيق؟',
    relancerMessage: 'ستعود إلى النموذج. سيتم فقدان التدقيق والمجموعة المعروضين.',
    relancerAnnuler: 'الاحتفاظ بهذا التدقيق',
    languageLabel: 'لغة التدقيق',
    languageHint: 'يبقى ملفك بلغته: التدقيق والحزمة هما ما يُكتب بهذه اللغة.',
    addCaptures: 'إضافة لقطات شاشة',
    analyse: 'تحليل ملفي',
    sourceConnect: 'حساب LinkedIn',
    sourceShots: 'لقطات الشاشة',
    capturesGras: 'حتى 5 لقطات',
    capturesSuite: ' من ملفك، بالترتيب الذي تريد أن تُقرأ به.',
    captureRemove: 'إزالة اللقطة %{n}',
    profil: { titre: 'العنوان', apropos: 'نبذة', experiences: 'الخبرات' },
    promesses: { audit: 'تدقيق مفصّل', kit: 'حزمة جاهزة للنسخ' },
    hint: { empty: 'أضف لقطة واحدة على الأقل لبدء التدقيق.', ready: 'كل شيء جاهز، يمكنك بدء التدقيق.', connect: 'اربط حسابك على LinkedIn لبدء التدقيق.', account: 'يُقرأ ملفك مباشرة من حسابك.' },
    missingKeywords: 'كلمات مفتاحية للإضافة',
    priorities: 'إجراءات ذات أولوية',
    connection: {
      secureTitle: 'اتصال آمن',
      secureBody: 'لا نصل إلا إلى المعلومات اللازمة لتحليل ملفك.',
      connected: 'تم ربط LinkedIn',
      connectedSub: 'اتصال آمن، دون كلمة مرور.',
      expired: 'انتهت جلسة LinkedIn. أعد الاتصال لتفعيلها من جديد.',
      unavailable: 'ربط LinkedIn متاح فقط في الاتحاد الأوروبي والمنطقة الاقتصادية الأوروبية وسويسرا.',
      unknown: 'حالة LinkedIn غير متاحة حاليًا.',
      failed: 'تعذّر الاتصال بـ LinkedIn. أعد المحاولة.',
      fallbackHint: 'لا تزال لقطات الشاشة قابلة للاستخدام.',
      eligibilityHint:
        'متاح في الاتحاد الأوروبي والمنطقة الاقتصادية الأوروبية وسويسرا. في أماكن أخرى، استخدم تبويب لقطات الشاشة — التحليل نفسه.',
      connect: 'ربط حسابي على LinkedIn',
      reconnect: 'إعادة الاتصال',
      disconnect: 'قطع الاتصال',
    },
    optimisation: {
      v1: 'النسخة 1 (مباشرة لمسؤول التوظيف)',
      v2: 'النسخة 2 (كلمات مفتاحية)',
      v3: 'النسخة 3 (مميِّزة)',
    },
  },

  linkedinKit: {
    ready: 'مجموعتك الجاهزة للنسخ',
    readyHint: 'اضغط «نسخ» على أي قسم، أو انسخ الحزمة كاملة، ثم الصقها في LinkedIn.',
    copy: 'نسخ',
    copied: 'تم النسخ',
    copyFailed: 'فشل',
    copyAll: 'نسخ الحزمة كاملة',
    kitCopied: 'تم نسخ الحزمة',
    kitCopyFailed: 'تعذّر النسخ: حدّد النص يدويًا.',
    copyLabel: 'نسخ: %{nom}',
    headline: 'العنوان',
    about: 'قسم «نبذة عن»',
    aboutShort: 'النسخة القصيرة',
    aboutFull: 'النسخة الكاملة',
    experiences: 'الخبرات الأساسية',
    skills: 'المهارات',
    skillsPinned: 'للتثبيت (5)',
    skillsExtra: 'مهارات تكميلية',
    search: 'إعدادات البحث',
    searchTitles: 'المسمّيات',
    searchContracts: 'العقود',
    searchLocations: 'المواقع',
    searchSectors: 'القطاعات',
    searchAvailability: 'التوفر',
    express: 'نصائح سريعة',
    photo: 'الصورة',
    banner: 'صورة الغلاف',
    url: 'الرابط',
    education: 'التعليم (للصق)',
    quality: 'مراقبة الجودة',
    keywordsAdded: 'كلمات مفتاحية مضافة',
    languagesRemoved: 'لغات أُزيلت (أقل من B2)',
    skillsNotProven: 'مهارات غير مُثبتة',
    toValidate: 'للتحقق',
    removed: 'عناصر محذوفة',
    recommendations: 'توصيات',
    recommended: 'موصى به',
    experienceN: 'الخبرة %{n}',
    recoAsk: 'توصية للطلب',
    recoAskWho: 'توصية للطلب (%{who})',
  },

  myLetter: {
    title: 'رسالة التحفيز الخاصة بي',
    subtitle: 'أنشئ رسالة التحفيز، ثم اطلب تحليلها.',
    pitchLead: 'رسالة مكيّفة مع الوظيفة المستهدفة تصنع الفرق.',
    arguments: {
      analysed: 'محلّلة وفق الوظيفة المستهدفة',
      skills: 'تُبرز مهاراتك',
      structure: 'بنية مقنعة',
      ready: 'جاهزة للإرسال',
    },
    createSection: 'إنشاء رسالتي',
    createTitle: 'إنشاء رسالة',
    createSubtitle: 'انطلاقًا من إحدى سيرك الذاتية',
    listSection: 'رسائلي',
    listCount: 'رسائلي · %{count}',
    seeAll: 'عرض الكل',
    newLetter: 'جديدة',
    listSubtitle: 'اعثر على رسائلك وأعد فتحها وأدِرها',
    improveSection: 'تحسين رسالتي',
    analyseTitle: 'تحليل رسالتي',
    analyseSubtitle: 'الدرجة والأخطاء المرصودة والأولويات',
    lastScore: 'آخر درجة',
    lastScoreNone: 'اطلب تحليل رسالة لاكتشاف درجتها.',
    savedCount: {
      zero: 'لا توجد رسالة محفوظة',
      one: 'رسالة واحدة محفوظة',
      other: 'الرسائل المحفوظة: %{count}',
    },
  },

  letterAnalyse: {
    importTitle: 'استيراد رسالتي',
    importSubtitle: 'PDF / Word',
    importPicked: 'الملف المستورد: %{name}',
    importRemove: 'إزالة الملف',
    orPaste: 'أو الصق نصها',
    rebuiltFromPdf: 'نص مُعاد بناؤه من ملف PDF المستورد.',
    title: 'تحليل رسالتي',
    subtitle: 'يقيّم الذكاء الاصطناعي رسالتك مقارنة بالوظيفة المستهدفة ويخبرك بما يجب تصحيحه أولًا.',
    resultTitre: 'تحليل رسالتي',
    tabSaved: 'رسائلي',
    tabImport: 'استيراد',
    tabPaste: 'لصق',
    importHint: 'PDF أو Word. ستُحفظ في رسائلي مع تحليلها.',
    pasteLabel: 'نص رسالتك',
    pastedTitle: 'رسالة ملصقة بتاريخ %{date}',
    cvCrossedNone: 'لا توجد سيرة ذاتية: لن يتم التحقق من الوقائع الواردة في رسالتك.',
    offerSub: 'اختياري، لتحليل أدق',
    optionsTitre: 'خيارات إضافية',
    optionsSousTitre: 'ملف المرشح',
    profilRepris: 'الملف مأخوذ من حسابك.',
    sourceSection: 'الرسالة المراد تحليلها',
    pickSaved: 'اختيار رسالة محفوظة',
    pickSavedEmpty: 'لا توجد رسالة محفوظة حاليًا',
    pasteTitle: 'لصق رسالة',
    pasteSubtitle: 'رسالة كُتبت في مكان آخر',
    pastePlaceholder: 'الصق نص رسالتك هنا…',
    pasteSaved: 'ستُحفظ رسالتك الملصقة في «رسائلي» مع تحليلها.',
    titleLabel: 'عنوان الرسالة (اختياري)',
    titlePlaceholder: 'مثال: مسؤول تواصل (تكوين بالتناوب)',
    offerLabel: 'الإعلان',
    offerHint: 'دون إعلان، يركّز التحليل على الوظيفة المستهدفة وتوقعات السوق.',
    offerPlaceholder: 'الصق الإعلان هنا…',
    profileLabel: 'ملفك',
    profileHint: 'يحدّد مستوى التقييم: لا نحكم على رسالة تلميذ في الإعدادي كما نحكم على رسالة مدير.',
    metierLabel: 'الوظيفة المستهدفة',
    metierPlaceholder: 'مثال: مسؤول تواصل',
    submit: 'تحليل رسالتي',
    loading: 'الذكاء الاصطناعي يحلّل رسالتك…',
    empty: 'أضف الرسالة المراد تحليلها.',
    failed: 'فشل التحليل. أعد المحاولة.',
    cvCrossed: 'تحليل مقارن مع سيرتك الذاتية الرئيسية.',
    cvNotCrossed: 'لا توجد سيرة ذاتية متاحة: لم يتم التحقق من الوقائع الواردة في الرسالة.',
    obsolete: 'عُدّلت هذه الرسالة منذ تحليلها. الدرجة تخص النسخة السابقة.',
    reanalyse: 'إعادة التحليل',
    improve: 'حسّن رسالتي',
    prioritiesSection: 'للتصحيح أولًا',
    errorsSection: 'الأخطاء المرصودة',
    strengthsSection: 'ما ينجح بالفعل',
    detailSection: 'التفاصيل حسب المعيار',
    coveredSection: 'المتطلبات المغطّاة',
    missingSection: 'المتطلبات غير المعالجة',
    incoherenceSection: 'التناقضات مع سيرتك الذاتية',
    faults: {
      zero: 'لم يُرصد أي خطأ',
      one: 'خطأ واحد مرصود',
      other: 'الأخطاء المرصودة: %{count}',
    },
    verdicts: {
      a_refaire: 'يجب إعادة كتابتها',
      a_retravailler: 'تحتاج إلى تحسين',
      envoyable_apres_corrections: 'قابلة للإرسال بعد التصحيح',
      solide: 'متينة',
      excellente: 'ممتازة',
    },
    criteres: {
      C1: 'الملاءمة للوظيفة',
      C2: 'الأدلة والتحديد',
      C3: 'التخصيص',
      C4: 'البنية والطول',
      C5: 'اللغة والأسلوب',
      C6: 'الأعراف والشكل',
    },
    niveaux: {
      insuffisant: 'غير كافٍ',
      fragile: 'هشّ',
      solide: 'متين',
      excellent: 'ممتاز',
    },
    gravites: {
      majeure: 'كبيرة',
      moyenne: 'متوسطة',
      mineure: 'طفيفة',
    },
  },

  // « Améliorer ma lettre » (1.2.0).
  coverLetterImprove: {
    title: 'حسّن رسالتي',
    blocked: 'تعذّر إتمام التحسين.',
    retry: 'أعد المحاولة',
    reponseInvalide: 'الرد غير مكتمل. أعد المحاولة بعد قليل.',
    saveFailed: 'تعذّر الحفظ.',
    copieTitre: '%{titre} (محسّنة)',
    tags: { hook: 'الافتتاحية', proof: 'الدليل', keywords: 'الكلمات المفتاحية', clarity: 'الوضوح', closing: 'الخاتمة', subject: 'الموضوع' },
    modif: { objet: 'موضوع الرسالة', paragraphe: 'الفقرة %{numero}', appliquer: 'تطبيق: %{nom}' },
    revue: {
      titre: {
        one: 'تعديل واحد مقترح',
        two: 'تعديلان مقترحان',
        few: '%{count} تعديلات مقترحة',
        many: '%{count} تعديلًا مقترحًا',
        other: '%{count} تعديل مقترح',
      },
      aide: 'اقبل كل تعديل أو ارفضه. لا تتغيّر رسالتك الحالية: اختياراتك تنتج رسالة جديدة تُضاف إلى «رسائلي».',
      longueur: 'متن الرسالة: %{avant} كلمة قبل، و%{apres} كلمة بعد.',
    },
    pied: {
      retenues: 'المحتفَظ بها: %{count} من %{total}',
      appliquer: {
        one: 'تطبيق تعديل واحد',
        two: 'تطبيق التعديلين',
        few: 'تطبيق %{count} تعديلات',
        many: 'تطبيق %{count} تعديلًا',
        other: 'تطبيق %{count} تعديل',
      },
      aucune: 'لم تُختر أي تعديلات',
    },
    vide: { titre: 'رسالتك قوية بالفعل', detail: 'لم يجد الذكاء الاصطناعي أي تعديل مفيد يقترحه. لم يُخصم شيء من حصتك.' },
    fait: { titre: 'تم حفظ الرسالة الجديدة', detail: 'رسالتك المحسّنة موجودة في «رسائلي». رسالتك الأصلية لم تتغيّر.', ouvrir: 'فتح الرسالة' },
  },

  letterList: {
    open: 'فتح',
    title: 'رسائلي',
    subtitle: 'اعثر على رسائلك المحفوظة وأعد فتحها وأدِرها.',
    loadError: 'تعذّر تحميل رسائلك. أعد المحاولة.',
    emptyTitle: 'لا توجد رسالة حاليًا.',
    emptyText: 'أنشئ أول رسالة انطلاقًا من إحدى سيرك الذاتية، وستُحفظ هنا.',
    emptyCta: 'إنشاء رسالتي',
    deleteTitle: 'حذف هذه الرسالة؟',
    deleteBody: 'سيتم حذف %{title} نهائيًا.',
  },

  letterCreate: {
    erreurFormat: 'تعذّرت كتابة الرسالة. أعد المحاولة: لم يُستخدم أي رصيد.',
    modifierAnnonce: 'تعديل الإعلان',
    change: 'تغيير',
    changeA11y: 'تغيير السيرة الذاتية',
    optionsTitre: 'خيارات إضافية',
    optionsSousTitre: 'الملف، الجهة، المستلم، البريد الإلكتروني',
    reprisDuCompte: 'الملف ونوع الطلب مأخوذان من حسابك. ما تختاره هنا سيُحفظ لرسالتك القادمة.',
    failed: 'تعذّر إنشاء الرسالة. حاول مرة أخرى.',
    annonceOr: 'أو استورد ملف الإعلان',
    annonceFile: 'PDF / Word',
    annonceFilePicked: 'الإعلان المستورد: %{name}',
    annonceFileRemove: 'إزالة الملف',
    title: 'إنشاء رسالة',
    subtitle: 'اختر سيرة ذاتية، والصق الإعلان المستهدف، واضبط النبرة.',
    resultTitle: 'رسالتك',
    resultSubtitle: 'أعد قراءتها، وتحقق مما يلزم، ثم حمّلها.',
    noCvTitle: 'تحتاج أولًا إلى سيرة ذاتية',
    noCvText:
      'تعتمد رسالتك على سيرتك الذاتية لاستخلاص خبراتك وأدلتك. أنشئ سيرة ذاتية أو استوردها، ثم عُد إلى هنا.',
    goToCv: 'الذهاب إلى سيرتي الذاتية',
    toConfirm: 'للتحقق قبل الإرسال',
    emailBlock: 'بريد إلكتروني مرافق',
    download: 'تحميل رسالتي',
    cvUsed: 'السيرة الذاتية المستخدمة',
    annonce: 'الإعلان المستهدف',
    annoncePlaceholder: 'الصق الإعلان هنا…',
    profil: 'الملف',
    type: 'نوع الطلب',
    ton: 'نبرة الرسالة',
    langue: 'لغة الرسالة',
    organisation: 'الجهة المستهدفة',
    organisationPlaceholder: 'مثال: ACME',
    metier: 'الوظيفة المستهدفة',
    metierPlaceholder: 'مثال: مسؤول مشروع',
    recipientName: 'المرسَل إليه — الاسم',
    recipientNamePlaceholder: 'اتركه فارغًا إن كنت لا تعرفه',
    recipientRole: 'المرسَل إليه — المنصب',
    recipientRolePlaceholder: 'مثال: مسؤول التوظيف',
    city: 'مدينتك',
    cityPlaceholder: 'مثال: Lyon',
    addEmail: 'إضافة بريد إلكتروني مرافق',
    generate: 'إنشاء رسالتي',
    hint: 'اختر سيرة ذاتية وأضف الإعلان لبدء الإنشاء.',
    defaultTitle: 'رسالة التحفيز',
    saveFailed: 'أُنشئت رسالتك لكن تعذّر حفظها.',
    exportFailed: 'فشل التصدير.',
    wordCount: 'عدد الكلمات: %{count}',
    profils: {
      collegien: 'تلميذ إعدادي (تدريب السنة الثالثة)',
      lyceen: 'تلميذ ثانوي',
      etudiant_jeune_diplome: 'طالب أو خريج جديد',
      actif_confirme: 'محترف ذو خبرة',
      manager_cadre_dirigeant: 'مدير أو قيادي',
      reconversion: 'تحوّل مهني',
    },
    types: {
      reponse_offre: 'رد على عرض',
      candidature_spontanee: 'طلب توظيف عفوي',
      mobilite_interne: 'تنقّل داخلي',
    },
    tons: {
      classique: 'كلاسيكي',
      chaleureux: 'ودود',
      direct: 'مباشر',
      entrepreneurial: 'ريادي',
      institutionnel: 'مؤسسي',
      executif: 'تنفيذي',
    },
    langues: { francais: 'الفرنسية', anglais: 'الإنجليزية' },
  },

  interview: {
    startFailed: 'تعذّر بدء المقابلة. حاول مرة أخرى.',
    noCv: 'ليس لديك سيرة ذاتية بعد. أنشئ سيرة ذاتية أولاً لبدء المقابلة.',
    title: 'مقابلة بالذكاء الاصطناعي',
    cvUsed: 'السيرة الذاتية المستخدمة',
    change: 'تغيير',
    changeA11y: 'تغيير السيرة الذاتية',
    hintEmpty: 'حدّد الوظيفة المستهدفة لبدء المقابلة.',
    loadError: 'تعذّر التحميل.',
    addCv: 'إضافة سيرة ذاتية أخرى',
    posteSection: 'الوظيفة المستهدفة',
    posteFile: 'إضافة وصف وظيفي',
    postePaste: 'لصق عرض عمل',
    posteExample: 'مثال: مسؤول تسويق',
    pickFile: 'PDF / Word',
    pickCaptures: 'لقطات الشاشة',
    offerPlaceholder: 'الصق نص العرض هنا…',
    companyType: 'نوع الشركة (اختياري)',
    companyTypePlaceholder: 'شركة ناشئة، مجموعة كبرى، مكتب استشارات، شركة صغيرة أو متوسطة…',
    adaptNote: 'يحلّل الذكاء الاصطناعي الوظيفة المستهدفة ويكيّف الأسئلة معها.',
    languageLabel: 'لغة المقابلة',
    start: 'ابدأ المقابلة',
    interlocutors: {
      recruiter: {
        label: 'مسؤول التوظيف',
        points: { motivation: 'الدافع', parcours: 'المسار', competences: 'المهارات' },
      },
      manager: {
        label: 'المدير',
        points: { terrain: 'الميدان', metiers: 'المهن', situation: 'وضعيات تطبيقية' },
      },
    },
    quitTitle: 'مغادرة المقابلة؟',
    quitBody: 'سيضيع تقدّمك.',
    quitCancel: 'متابعة المقابلة',
    quitConfirm: 'مغادرة',
    muteVoice: 'كتم صوت مسؤول التوظيف',
    voixActivee: 'الصوت مفعّل',
    downloadPdf: 'تحميل ملف PDF',
    pdfErreur: 'تعذّر إنشاء ملف PDF. أعد المحاولة.',
    voixCoupee: 'الصوت مكتوم',
    voixReviendra: {
      lundi: 'صوت الهاتف حاليًا: سيعود الصوت الواقعي يوم الاثنين.',
      mois: 'صوت الهاتف حاليًا: سيعود الصوت الواقعي في أول الشهر القادم.',
      entretien: 'صوت الهاتف حتى نهاية هذه المقابلة: سيعود الصوت الواقعي في المقابلة القادمة.',
      bientot: 'صوت الهاتف حاليًا: سيعود الصوت الواقعي قريبًا.',
    },
    voixRestante: {
      semaine: 'الصوت الواقعي — المقابلات المتبقية هذا الأسبوع: %{n}.',
      semaineUn: 'الصوت الواقعي: تبقّت مقابلة واحدة هذا الأسبوع.',
      semaineAucun: 'الصوت الواقعي: انتهى رصيد هذا الأسبوع. يتولى صوت الهاتف المهمة حتى يوم الاثنين.',
      mois: 'الصوت الواقعي — المقابلات المتبقية هذا الشهر: %{n}.',
      moisUn: 'الصوت الواقعي: تبقّت مقابلة واحدة هذا الشهر.',
      moisAucun: 'الصوت الواقعي: انتهى رصيد هذا الشهر. يتولى صوت الهاتف المهمة حتى أول الشهر القادم.',
    },
    unmuteVoice: 'تفعيل صوت مسؤول التوظيف',
    listen: 'استماع',
    finish: 'إنهاء المقابلة',
    answerPlaceholder: 'إجابتك…',
    stopDictation: 'إيقاف الإدخال الصوتي',
    startDictation: 'الإجابة بالصوت',
    clearAnswer: 'مسح',
    send: 'إرسال',
    strengths: 'نقاط القوة',
    improvements: 'للتحسين',
    tips: 'نصائح',
    restart: 'البدء من جديد',
    phasePresentation: 'مرحلة التقديم',
    phaseOngoing: 'المقابلة جارية',
    feedbackHeader: 'تقييم أدائك',
    overallLabel: 'التقييم العام',
    dureeMin: '%{n} د',
  },

  profile: {
    fallbackName: 'ملفك',
    personalInfo: 'المعلومات الشخصية',
    personalInfoSubtitle: 'أدِر معلوماتك وتفضيلاتك',
    subscription: 'اشتراكي',
    subscriptionSubtitle: 'أدِر خطتك وفواتيرك',
    activity: 'نشاطي هذا الشهر',
    coaching: 'التوجيه المهني الخاص بي',
    coachingHint: 'يُتفق على الموعد عبر البريد الإلكتروني.',
    bookSession: 'حجز جلسة',
    feedbackTitle: 'هل لديك أسئلة أو اقتراحات لتحسين التطبيق؟',
    feedbackSubtitle: 'رأيك يهمّنا!',
    avatarSheet: 'صورة الملف الشخصي أو خلفيتها',
    importFailed: 'تعذّر الاستيراد. أعد المحاولة.',
    stats: {
      cvCreated: 'السير الذاتية المُنشأة',
      interviews: 'مقابلات الذكاء الاصطناعي',
      cvScores: 'تقييمات السيرة الذاتية',
      linkedinScores: 'تقييمات LinkedIn',
    },
    noSession: 'لا توجد جلسة مبرمجة',
    sessionFallback: 'جلسة',
    lastSession: 'آخر جلسة حُجزت في %{date} — %{formule}',
  },

  personalInfo: {
    firstName: 'الاسم الأول',
    firstNamePlaceholder: 'اسمك الأول',
    lastName: 'اسم العائلة',
    lastNamePlaceholder: 'اسم عائلتك',
    email: 'البريد الإلكتروني للتواصل',
    emailPlaceholder: 'you@example.com',
    phone: 'الهاتف',
    phonePlaceholder: '06 12 34 56 78',
    address: 'العنوان / المدينة',
    addressPlaceholder: 'مثال: Paris',
    linkedin: 'رابط LinkedIn',
    linkedinPlaceholder: 'https://www.linkedin.com/in/…',
  },

  profileCompletion: {
    title: 'شيء أخير',
    birthDatePlaceholder: 'DD/MM/YYYY',
    continue: 'متابعة',
    later: 'لاحقًا',
    saveFailed: 'تعذّر الحفظ.',
  },

  notifications_screen: {
    title: 'الإشعارات',
    empty: 'لا توجد إشعارات جديدة حاليًا.',
    settingsSubtitle: 'تذكيراتك ونصائح للتقدم في بحثك.',
    tips: 'نصائح',
  },

  auth: {
    signup: {
      title: 'أنشئ حسابك،',
      titleAccent: 'وانطلق.',
      subtitle: 'حسّن سيرتك الذاتية وحضّر لمقابلاتك وتقدّم نحو عقدك.',
      cta: 'إنشاء حسابي',
    },
    signupEtablissement: {
      title: 'أنشئوا حساب',
      titleAccent: 'المؤسسة',
      subtitle: 'وامنحوا طلابكم أداة لإعداد ترشيحاتهم.',
    },
    signupPartenaire: {
      title: 'أنشئوا حسابكم',
      titleAccent: 'كشريك',
      subtitle: 'وامنحوا الشباب الذين ترافقونهم أداة لإعداد ترشيحاتهم.',
    },
    signin: {
      title: 'واصل',
      titleAccent: 'التميّز.',
      subtitle: 'استأنف تحسيناتك وتقدّم نحو مقابلتك القادمة.',
      cta: 'تسجيل الدخول',
    },
    pending:
      'إذا لم يكن هذا العنوان مستخدمًا بالفعل، فستتلقى بريدًا إلكترونيًا للتأكيد. لا تنسَ التحقق من مجلد الرسائل غير المرغوب فيها.',
    invalidEmail: 'عنوان بريد إلكتروني غير صالح.',
    invalidPassword: 'كلمة مرور غير صالحة.',
    emailLabel: 'البريد الإلكتروني',
    emailPlaceholder: 'name@email.com',
    passwordLabel: 'كلمة المرور',
    hidePassword: 'إخفاء كلمة المرور',
    showPassword: 'إظهار كلمة المرور',
    tabSignin: 'تسجيل الدخول',
    tabSignup: 'إنشاء حساب',
    forgot: {
      link: 'نسيت كلمة المرور؟',
      title: 'استعد',
      titleAccent: 'وصولك.',
      subtitle: 'أدخل بريدك الإلكتروني وسنرسل إليك رابطًا لاختيار كلمة مرور جديدة.',
      cta: 'إرسال الرابط',
      sent: 'إذا كان هناك حساب بهذا العنوان، فقد أُرسلت إليك رسالة بريد إلكتروني تتضمن رابطًا لاختيار كلمة مرور جديدة. تحقق من البريد المزعج وافتح الرابط من هذا الهاتف.',
      back: 'العودة إلى تسجيل الدخول',
    },
    newPassword: {
      title: 'اختر',
      titleAccent: 'كلمة مرورك الجديدة.',
      subtitle: 'ستحل محل كلمة المرور القديمة.',
      newLabel: 'كلمة المرور الجديدة',
      confirmLabel: 'تأكيد كلمة المرور',
      tooShort: 'يجب أن تتكون كلمة المرور من %{min} أحرف على الأقل.',
      mismatch: 'كلمتا المرور غير متطابقتين.',
      cta: 'حفظ',
      cancel: 'إلغاء وتسجيل الخروج',
    },
    or: 'أو',
    google: 'المتابعة باستخدام Google',

    error: {
      invalidCredentials: 'البريد الإلكتروني أو كلمة المرور غير صحيحة.',
      emailNotConfirmed:
        'لم يتم تأكيد حسابك بعد. افتح الرابط الذي أرسلناه إليك عبر البريد الإلكتروني — لا تنسَ التحقق من مجلد الرسائل غير المرغوب فيها.',
      emailInvalid: 'عنوان البريد الإلكتروني هذا غير صالح.',
      validationFailed: 'تحقق من بريدك الإلكتروني وكلمة المرور.',
      weakPassword: 'كلمة المرور ضعيفة جدًا. اختر كلمة أطول تحتوي على أرقام وحروف.',
      rateLimit: 'محاولات كثيرة جدًا. أعد المحاولة بعد بضع دقائق.',
      userBanned: 'هذا الحساب معلّق. راسلنا لمعرفة المزيد.',
      network: 'تعذّر الاتصال. تحقق من اتصالك بالإنترنت وأعد المحاولة.',
      confirmExpired:
        'رابط التأكيد هذا لم يعد صالحًا. أعد إنشاء حسابك بالعنوان نفسه لتتلقى رابطًا جديدًا.',
      confirmWrongDevice: 'افتح هذا الرابط من الهاتف الذي أنشأت عليه حسابك.',
      resetExpired: 'لم يعد رابط إعادة التعيين صالحًا. اطلب رابطًا جديدًا عبر «نسيت كلمة المرور؟».',
      resetWrongDevice: 'افتح آخر رسالة بريد وصلتك، من الهاتف الذي طلبت منه إعادة التعيين.',
      samePassword: 'اختر كلمة مرور مختلفة عن القديمة.',

      serverSignup:
        'لا نستطيع إنشاء حسابك حاليًا — المشكلة من جهتنا، لا من جهتك. أعد المحاولة بعد بضع دقائق، أو تابع باستخدام %{providers}.',
      serverSignin:
        'لا نستطيع تسجيل دخولك حاليًا — المشكلة من جهتنا، لا من جهتك. أعد المحاولة بعد بضع دقائق، أو تابع باستخدام %{providers}.',
      serverOauth:
        'لم يكتمل تسجيل الدخول — المشكلة من جهتنا، لا من جهتك. أعد المحاولة بعد بضع دقائق، أو سجّل الدخول ببريدك الإلكتروني وكلمة المرور.',
      popupBlocked:
        'حظر متصفحك نافذة تسجيل الدخول عبر Google. اسمح بالنوافذ المنبثقة لهذا الموقع، ثم أعد تسجيل الدخول — أو سجّل الدخول ببريدك الإلكتروني وكلمة المرور.',
      appleUnknown:
        'لم يكتمل تسجيل الدخول عبر Apple. تحقق من أنك مسجّل الدخول إلى iCloud على هذا الجهاز وأن التحقق بخطوتين مفعّل على معرّف Apple الخاص بك. وإلا، فسجّل الدخول ببريدك الإلكتروني وكلمة المرور.',
      serverSignout: 'لم يكتمل تسجيل الخروج. أعد المحاولة بعد بضع دقائق.',
      serverConfirm:
        'لا نستطيع تأكيد حسابك حاليًا — المشكلة من جهتنا، لا من جهتك. أعد المحاولة بعد بضع دقائق، أو تابع باستخدام %{providers}.',
      serverReset:
        'لا نستطيع إعادة تعيين كلمة مرورك حاليًا — المشكلة من جهتنا، لا من جهتك. أعد المحاولة بعد بضع دقائق.',

      googleStart: 'لا نستطيع فتح تسجيل الدخول عبر Google. أعد المحاولة بعد بضع دقائق.',
      googleResponse: 'استجابة Google غير مكتملة. أعد المحاولة.',
      appleResponse: 'استجابة Apple غير مكتملة. أعد المحاولة.',
      deleteFailed:
        'لم يُحذف حسابك: فشلت العملية قبل حذف أي شيء، وبياناتك سليمة. أعد المحاولة بعد بضع دقائق.',
      deleteAppleCancelled: 'أُلغي الحذف: أكّد مع Apple لحذف حسابك.',

      providersIos: 'Google أو Apple',
      providersAndroid: 'Google',
    },
  },

  onboarding: {
    highlights: { cv: 'السيرة الذاتية', linkedin: 'LinkedIn', interview: 'المقابلات', coach: 'الموجّه' },
    title: 'ملفك يستحق',
    mots: {
      reponses: 'ردودًا.',
      cv: 'سيرة ذاتية تنجح.',
      entretien: 'مقابلة.',
      contrat: 'عقدًا.',
    },
    sousTitre: 'نرافقك من السيرة الذاتية حتى توقيع عقدك.',
    wheel: {
      cv: 'سيرة ذاتية ATS',
      linkedin: 'LinkedIn',
      interview: 'مقابلات بالذكاء الاصطناعي',
      coach: 'موجّه خبير',
      offers: 'عروض',
      letter: 'رسالة',
      center: 'عقدك',
    },
    quickSignup: 'تسجيل سريع',
    dataProtected: 'بيانات محمية',
    createAccount: 'إنشاء حسابي',
    haveAccount: 'لديّ حساب بالفعل',
  },


  acceptTerms: {
    titleOutdated: 'تغيّرت شروطنا',
    titleFirst: 'أوشكت على الانتهاء ✨',
    changedPrefix: 'تم تحديث ',
    changedTermsLink: 'شروط الاستخدام',
    changedMiddle: ' و',
    changedPrivacyLink: 'سياسة الخصوصية',
    changedSuffix:
      ' الخاصة بنا (الإصدار %{version}). أعد قراءتها، ثم وافق عليها لمواصلة استخدام التطبيق.',
    firstPrefix: 'بسّطنا شروطنا لكي ',
    firstAccent: 'نحمي بياناتك بشكل أفضل',
    pillars: {
      data: 'بيانات\nمحمية',
      transparency: 'شفافية\nتامة',
      consent: 'لا إجراء\nدون موافقتك',
    },
    docTerms: 'شروط الاستخدام',
    docPrivacy: 'سياسة الخصوصية',
    accept: 'متابعة',
    refuseOutdated: 'الرفض وتسجيل الخروج',
    refuseFirst: 'لاحقًا، تسجيل الخروج',
    help: 'هل تحتاج إلى مساعدة؟',
    deleteAccount: 'حذف حسابي',
    saveFailed: 'تعذّر الحفظ. أعد المحاولة.',
  },

  legalHeaders: {
    documentsEnFrancais: 'الوثائق متاحة بالفرنسية',
    privacy: 'الخصوصية والبيانات',
    terms: 'شروط الاستخدام',
    dataDeletion: 'حذف الحساب',
  },

  plans: {
    free: {
      tagline: 'للبداية',
      price: 'مجاني',
      period: 'مدى الحياة',
      entretiens: 'مقابلة واحدة بالذكاء الاصطناعي / شهر، بصوت طبيعي لمسؤول التوظيف',
      features: [
        'تقييم واحد واستيراد واحد للسيرة الذاتية / شهر',
        'تدقيق واحد لـ LinkedIn / شهر',
        'رسالة واحدة وتحليل واحد للرسائل / شهر',
        'تصدير واحد بصيغة PDF / شهر',
        'لا إنشاء للسيرة الذاتية بالذكاء الاصطناعي',
        'قوالب أساسية',
      ],
      featuresOffres: ['عروض التكوين بالتناوب والعمل: وصول مجاني', 'طلبان بالذكاء الاصطناعي / شهر (تبويب العروض)'],
    },
    plus: {
      tagline: 'التوازن المثالي',
      period: '/ شهر',
      entretiens: '8 مقابلات بالذكاء الاصطناعي / شهر',
      voix: 'مقابلتان أسبوعيًا بصوت واقعي',
      features: [
        '5 عمليات إنشاء و5 عمليات استيراد للسيرة الذاتية / شهر',
        '5 تقييمات للسيرة الذاتية / شهر',
        '5 عمليات تدقيق لـ LinkedIn / شهر',
        '5 رسائل و5 تحليلات للرسائل / شهر',
        '20 إعادة صياغة للأقسام بالذكاء الاصطناعي / شهر',
        'تصدير غير محدود وكل القوالب',
      ],
      featuresOffres: ['15 طلبًا بالذكاء الاصطناعي / شهر (تبويب العروض)'],
    },
    pro: {
      tagline: 'لتذهب ببحثك حتى النهاية',
      period: '/ شهر',
      entretiens: '20 مقابلة بالذكاء الاصطناعي / شهر',
      voix: '5 مقابلات أسبوعيًا بصوت واقعي',
      ecart: '20 مقابلة و60 طلبًا بالذكاء الاصطناعي شهريًا',
      ecartSansOffres: '20 مقابلة بالذكاء الاصطناعي شهريًا',
      features: [
        'غير محدود: السير الذاتية والتقييمات وتدقيقات LinkedIn والرسائل والتحليلات',
        'إعادة صياغة بالذكاء الاصطناعي وتصدير غير محدودين',
        'قوالب مميزة',
      ],
      featuresOffres: ['60 طلبًا بالذكاء الاصطناعي / شهر (تبويب العروض)'],
    },
    school: {
      features: [
        'كل الميزات، لجميع الطلاب',
        'مثالي للمدارس ومراكز تكوين المتمهّنين (CFA)',
        'الفوترة حسب عدد المستخدمين',
        'ترخيص',
        'لوحة متابعة للمؤسسة',
        'تقرير ربع سنوي',
        'تخصيص',
      ],
    },
  },

  plansExtra: {
    proAnnual: {
      name: 'اشتراك Pro السنوي',
      period: '/سنة',
      badge: '−%{pourcentage}%',
      equivalent: 'أي ما يعادل %{monthly} شهريًا على مدى 12 شهرًا',
      cta: 'اختيار الاشتراك السنوي',
    },
  },

  paywall: {
    mentionAbonnement: 'اشتراك شهري أو سنوي حسب الباقة المختارة، يتجدد تلقائيًا للمدة نفسها ما لم يُلغَ قبل 24 ساعة على الأقل من نهاية الفترة الحالية. يمكنك الإلغاء في أي وقت من إدارة الاشتراكات في App Store أو Google Play، ويسري الإلغاء في نهاية الفترة الحالية.',
    tonPlan: 'باقتك',
    bientotSurPlateforme: 'ستتوفر الاشتراكات قريبًا على هذه المنصة.',
    currentPlan: 'خطتك الحالية: ',
    title: 'انتقل إلى المستوى التالي',
    capReached: 'لم يتبقَّ لك أي استخدام هذا الشهر. يتجدد رصيدك في %{date}.',
    notIncluded: 'هذه الميزة غير مشمولة في خطتك الحالية.',
    tabIndividual: 'فردي',
    tabSchools: 'المدارس',
    restore: 'استعادة مشترياتي',
    contact: 'تواصل معنا',
    recommended: 'موصى به',
    subscribe: 'الاشتراك',
    pendingApproval: 'الشراء بانتظار الموافقة.',
    prixIndisponibles: 'الأسعار غير متاحة حالياً. حاول مرة أخرى بعد قليل.',
    genericError: 'حدث خطأ.',
    activated: 'تم تفعيل الاشتراك 🎉',
    restored: 'تمت استعادة المشتريات.',
    activating: 'تم استلام الشراء. جارٍ تفعيل اشتراكك…',
    activationEnCours: 'تم استلام الشراء. يجري تفعيل اشتراكك، وقد يستغرق ذلك لحظات.',
    restoreEnCours: 'تم إرسال طلب الاستعادة. إذا كان هناك اشتراك مرتبط بحسابك فسيظهر بعد لحظات.',
    schoolMailSubject: 'Offre établissement Horméia',
  },

  coaching: {
    tarifsIndisponibles: 'الأسعار غير متاحة مؤقتًا.',
    reessayer: 'إعادة المحاولة',
    montantIncoherent:
      'الدفع غير متاح مؤقتًا: السعر المعروض لا يطابق المبلغ المراد خصمه. نفضّل عدم اقتطاع أي شيء حتى تُحل المشكلة.',
    retourSuccesTitre: 'تم تأكيد الدفع',
    retourSuccesCorps:
      'تم استلام دفعتك. ستتلقى بريدًا إلكترونيًا للتأكيد يتضمن تفاصيل جلستك.',
    retourEnCoursTitre: 'الدفع قيد المعالجة',
    retourEnCoursCorps:
      'لم يؤكد مصرفك الدفع بعد، وهذا أمر طبيعي مع بعض وسائل الدفع. لا تكرر العملية: سيُخصم منك المبلغ مرتين. ستتلقى بريدًا إلكترونيًا فور التأكيد.',
    retourEchecTitre: 'تم رفض الدفع',
    retourEchecCorps:
      'لم يكتمل الدفع ولم يُخصم منك أي مبلغ. يمكنك إعادة المحاولة بوسيلة الدفع نفسها أو بوسيلة أخرى.',
    retourAnnuleTitre: 'تم إلغاء الدفع',
    retourAnnuleCorps: 'أوقفت عملية الدفع ولم يُخصم منك أي مبلغ. موعدك لا يزال متاحًا.',
    retourVersCoaching: 'العودة إلى التوجيه المهني',
    title: 'التوجيه المهني',
    pickSlot: 'اختيار موعدي',
    soon: 'متاح قريبًا',
    slotBeforePayment: 'يُؤكَّد موعدك قبل أي دفع.',
    howTitle: 'كيف تسير الأمور',
    step1: 'تختار موعدك',
    step2: 'تؤكد حجزك',
    step3: 'تدفع ثمن جلستك',
    pickFormule: 'اختر باقتك',
    formuleNotFound: 'الباقة غير موجودة. عُد إلى الخلف واختر باقة.',
    reserve: 'حجز',
    checkingSlot: 'نتحقق من موعدك في جدول المواعيد…',
    slotConfirmed: 'تم تأكيد موعدك. لم يتبقَّ سوى إتمام الدفع.',
    chosenFormule: 'الباقة المختارة',
    email: 'البريد الإلكتروني',
    searching: 'ما تبحث عنه (اختياري)',
    payUnavailable:
      'الدفع غير متاح مؤقتاً. حاول مرة أخرى لاحقاً.',
    paid: 'تم الدفع بنجاح ✓. تم تأكيد حجزك، وسيتواصل معك Julien.',
    processing: 'جارٍ معالجة دفعتك. لا تدفع مرة ثانية: ستصلك رسالة تأكيد بمجرد التحقق منها.',
    securePayment: 'دفع آمن بالبطاقة (Stripe). لن يُخصم منك المبلغ إلا مرة واحدة.',
    nameRequired: 'أدخل اسمك.',
    invalidEmail: 'عنوان بريد إلكتروني غير صالح.',
    paymentFailed: 'تعذّر الدفع. أعد المحاولة.',
    paymentFailedShort: 'تعذّر الدفع.',
  },

  cvCreateFlow: {
    titre: 'إنشاء سيرة ذاتية',
    etapeSur: '%{n} من %{total}',
    step1Titre: 'ما الوظيفة التي تستهدفها؟',
    step1Sous: 'يكيّف الذكاء الاصطناعي السيرة الذاتية كلها لهذه الوظيفة.',
    step2Titre: 'مسيرتك',
    step2Sous: 'لا حاجة لصياغة متقنة: الذكاء الاصطناعي يعيد الصياغة عنك.',
    step3Titre: 'للختام',
    step3Sous: 'كل شيء اختياري، يمكنك إنشاء سيرتك الآن.',
    nameFromProfile: 'من ملفك الشخصي',
    importTitre: 'لديك سيرة ذاتية بالفعل؟',
    importSous: 'استوردها، فهذا أسرع.',
    roleRequired: 'حدّد الوظيفة المستهدفة للمتابعة.',
    tuileProjet: 'مشروع',
    tuileAutre: 'شيء آخر',
    optionnel: 'اختياري',
    ajoute: 'تمت الإضافة',
    nameLabel: 'الاسم في السيرة الذاتية',
    namePlaceholder: 'مثال: Jean Dupont',
    languageLabel: 'لغة السيرة الذاتية',
    languageHint: 'يمكنك الإجابة بالفرنسية والحصول على سيرة ذاتية بالإنجليزية، أو العكس.',
    contratLabel: 'أنت تبحث عن…',
    contratHint: 'اختياري. اضغط مرة أخرى للإلغاء.',
    contrats: { alternance: 'تكوين بالتناوب', stage: 'تدريب', cdi: 'عقد دائم (CDI)', cdd: 'عقد محدد المدة (CDD)' },
    generate: 'إنشاء سيرتي الذاتية',
    next: 'متابعة',
    buildingTitle: 'نُعدّ سيرتك الذاتية.',
    buildingHint: 'بضع ثوانٍ فقط، نعدك بذلك.',
    editTitle: 'تعديل سيرتي الذاتية',
    cancel: 'إلغاء',
    save: 'حفظ',
    readyTitle: 'سيرتك الذاتية جاهزة',
    atsScore: 'درجة ATS',
    edit: 'تعديل',
    saving: 'جارٍ الحفظ…',
    retrySave: 'إعادة محاولة الحفظ',
    download: 'تصدير سيرتي الذاتية',
    condensePourUnePage: 'محتوى مختصر ليتسع في صفحة واحدة',
    aiAdvice: 'نصائح الذكاء الاصطناعي',
    targetOffer: 'استهداف عرض (اختياري)',
    offerPlaceholder: 'الصق العرض هنا…',
    offerFile: 'العرض (PDF/Word)',
    offerCaptures: 'لقطات الشاشة',
    recommendCta: 'توصيات لهذا العرض',
    recommendSection: 'توصيات لهذا العرض',
    testInScore: 'اختبر هذه السيرة الذاتية في «قيّم سيرتي الذاتية»',
    skillPlaceholder: 'مثال: SQL',
    interestPlaceholder: 'مثال: الجري في المسارات',
    noterCe: 'قيّم هذه السيرة الذاتية',
    savedIn: 'تم الحفظ في «سيَري الذاتية»',
    editsSaved: 'تم حفظ التعديلات',
    saveFailed: 'تم إنشاء سيرتك الذاتية لكن تعذّر حفظها.',
    editSaveFailed: 'تعذّر الحفظ.',
    offerHint: 'أضف إعلان وظيفة: يقترح الذكاء الاصطناعي كيف تُبرز مسيرتك لهذه الوظيفة (دون اختلاق أي شيء). يبقى القرار لك في تطبيقه عبر التعديل.',
  },

  help: {
    title: 'المساعدة والتواصل',
    faqSection: 'الأسئلة الشائعة',
    contactSection: 'تواصل معنا',
    writeUs: 'راسلنا',
    sendEmail: 'إرسال بريد إلكتروني',
    mailSubject: 'Aide — Horméia',
    faq: {
      ats: {
        q: 'كيف تُحسب درجة ATS الخاصة بي؟',
        a: 'يحلّل الذكاء الاصطناعي سيرتك الذاتية وفق المعايير التي تفضّلها برامج فرز الطلبات (ATS): وجود الكلمات المفتاحية للوظيفة، وبنية واضحة، وأقسام معيارية، ونتائج مدعومة بالأرقام. تعكس الدرجة هذا التوافق وتُرفق بنصائح عملية لتحسينها.',
      },
      privacy: {
        q: 'هل بياناتي سرية؟',
        a: 'تُستخدم سيرك الذاتية ومعلوماتك لتشغيل ميزات التطبيق (التقييم، الإنشاء، المقابلة). ستُنشر قريبًا صفحة مخصّصة للخصوصية وإدارة بياناتك؛ وحتى ذلك الحين، راسلنا لأي سؤال.',
      },
      coaching: {
        q: 'كيف يعمل التوجيه المهني؟',
        a: 'التوجيه المهني مرافقة بشرية مع Julien Dauzats، خبير الموارد البشرية — وهو منفصل عن ميزات الذكاء الاصطناعي. تختار باقة، وتحجز من التطبيق، ويجري اللقاء عبر مكالمة فيديو.',
      },
    },
  },

  misc: {
    personalInfoTitle: 'المعلومات الشخصية',
    save: 'حفظ',
    coachNamePlaceholder: 'اسمك',
    coachGoalPlaceholder: 'مثال: التحضير لمقابلة في مجال البيانات الأسبوع المقبل',
    atsCompatible: 'متوافق مع ATS',
    completeProfile: 'أكمل ملفك',
    completeProfileCta: 'إكمال ملفي',
    later: 'لاحقًا',
    feedbackQuestion: 'هل كانت هذه النتيجة مفيدة لك؟',
    send: 'إرسال',
    importPhoto: 'استيراد صورة',
    orColour: 'أو خلفية ملوّنة',
    add: 'إضافة',
    yearPlaceholder: 'السنة',
    comingSoon: 'متاح قريبًا.',
  },

  progress: {
    linkedinKitSections: [
      'أكتب عنوانك…',
      'أكتب قسم «نبذة عن» الخاص بك…',
      'أعيد صياغة خبراتك…',
      'أختار مهاراتك…',
      'أضبط إعدادات البحث الخاصة بك…',
      'أُعدّ نصائحك السريعة…',
      'أضع اللمسات الأخيرة على حزمتك…',
    ],

    linkedinSections: [
      'أقرأ ملفك…',
      'أرصد الأخطاء الشائعة…',
      'أُعدّ درجتك وأولوياتك…',
    ],

    elapsed: {
      start: 'نُعدّ سيرتك الذاتية…',
      mid: 'بضع ثوانٍ أخرى…',
      long: 'الأمر يستغرق وقتًا أطول من المعتاد، نواصل العمل.',
    },
    letter: {
      start: 'نكتب رسالتك…',
      mid: 'بضع ثوانٍ أخرى…',
      long: 'الأمر يستغرق وقتًا أطول من المعتاد، نواصل العمل.',
    },
    linkedin: {
      start: 'نحلّل ملفك…',
      mid: 'بضع ثوانٍ أخرى…',
      long: 'الأمر يستغرق وقتًا أطول من المعتاد، نواصل العمل.',
    },
    improve: {
      start: 'نحسّن سيرتك الذاتية…',
      mid: 'بضع ثوانٍ أخرى…',
      long: 'الأمر يستغرق وقتًا أطول من المعتاد، نواصل العمل.',
    },
    improveLetter: {
      start: 'نحسّن رسالتك…',
      mid: 'بضع ثوانٍ أخرى…',
      long: 'الأمر يستغرق وقتًا أطول من المعتاد، نواصل العمل.',
    },
    received: 'تم الاستلام',
    saved: 'تم الحفظ',
  },

  // Licence accordée par l'établissement de l'étudiant (lot 4 du back-office). Aucun lien d'achat ici.
  licenceEcole: {
    title: 'ترخيص المدرسة',
    loading: 'جارٍ التحميل…',
    loadError: 'تعذّر تحميل ترخيصك. حاول مرة أخرى.',
    retry: 'إعادة المحاولة',
    intro: 'هل أرسلت لك مدرستك رمز دعوة؟ أدخله لتفعيل ترخيصك.',
    codeLabel: 'رمز الدعوة',
    codePlaceholder: 'XXXX-XXXX-XXXX',
    activer: 'تفعيل ترخيصي',
    activating: 'جارٍ التفعيل…',
    voitTitre: 'ما تراه مدرستك',
    voit: [
      'وضعك (قيد البحث، بعقد، عقد منتهٍ) وتواريخك المهمة، كما أدخلتها مدرستك.',
      'إجراءات المرافقة التي قامت بها من أجلك: مكالمات، لقاءات، عروض مرسلة.',
      'نشاطك في التطبيق: الأيام التي تستخدمه فيها. يظهر فور تفعيل ترخيصك، لأن مدرستك هي التي توفره لك.',
    ],
    neVoitPas: 'لا ترى أبدًا محتوى سِيَرك الذاتية أو رسائلك أو مقابلاتك.',
    active: 'الترخيص نشط',
    via: 'مقدَّم من %{nom}',
    employeurTitre: 'شركتك',
    employeurContrat: 'ربطتك مدرستك بـ %{nom} منذ %{date}.',
    employeurVoitTitre: 'ما ستراه شركتك إذا سمحت بذلك',
    employeurVoit: [
      'اسمك وتخصصك ومدرستك وتواريخ عقدك.',
      'وضعك (قيد البحث، بعقد، عقد منتهٍ).',
    ],
    employeurNeVoitPas: 'لا ترى أبدًا بريدك الإلكتروني ولا سجل مرافقة مدرستك ولا طلباتك ولا نشاطك في التطبيق، ولا محتوى سِيَرك الذاتية أو رسائلك أو مقابلاتك.',
    employeurConsentTitre: 'مشاركة وضعي مع شركتي',
    employeurConsentBody: 'معطَّل افتراضيًا. يمكنك سحبه في أي وقت: لا يتغير عقدك ولا ترخيصك. لا ترى مدرستك سوى ما إذا كنت قد وافقت أم لا.',
    employeurConsentError: 'تعذّر حفظ اختيارك. حاول مرة أخرى.',
    succes: 'تم تفعيل ترخيص %{nom}.',
    erreurs: {
      code_invalide: 'هذا الرمز غير صالح. تحقّق منه أو اطلب رمزًا جديدًا من مدرستك.',
      invitation_expiree: 'انتهت صلاحية هذه الدعوة. اطلب دعوة جديدة من مدرستك.',
      abonnement_actif: 'لديك اشتراك نشط بالفعل. ألغِه من إعدادات App Store أو Google Play ثم حاول مرة أخرى.',
      plus_de_sieges: 'لا توجد مقاعد متاحة لدى مدرستك. تواصل معها.',
      organisation_inactive: 'ترخيص مدرستك غير نشط حاليًا. تواصل معها.',
      inconnu: 'تعذّر التفعيل الآن. حاول مرة أخرى.',
    },
  },

  settings: {
    title: 'الإعدادات',
    sections: {
      account: 'الحساب',
      preferences: 'التفضيلات',
      admin: 'الإدارة',
      danger: 'منطقة الخطر',
    },
    privacy: 'الخصوصية والبيانات',
    licenceEcole: 'ترخيص المدرسة',
    dataDeletion: 'حذف الحساب والبيانات',
    notifications: 'الإشعارات',
    adminDashboard: 'لوحة تحكم المشرف',
    signOut: 'تسجيل الخروج',
    deleteAccount: 'حذف حسابي',
    deleting: 'جارٍ الحذف…',
    deleteFailedTitle: 'تعذّر الحذف',
    deleteConfirmTitle: 'حذف حسابك؟',
    deleteConfirmBody: 'سيُعطَّل حسابك فورًا وتُحذف بياناتك نهائيًا بعد 29 يومًا. لا يُلغى اشتراكك تلقائيًا: ألغِه من إعدادات App Store أو Google Play.',
    language: {
      label: 'لغة التطبيق',
      hint: 'تحتفظ المستندات التي تنشئها بلغتها الخاصة، التي تختارها عند إنشائها.',
      relanceTitre: 'إعادة تشغيل التطبيق؟',
      relanceTexte: 'للانتقال إلى هذه اللغة، يجب إعادة تشغيل Horméia. تبقى سيرك الذاتية ورسائلك وإعداداتك المحفوظة، أما المقابلة الجارية أو أي نص غير محفوظ فسيُفقد.',
      relanceOk: 'إعادة التشغيل',
    },
  },
  update: {
    banner: {
      title: 'يتوفر إصدار جديد',
      body: 'حدّث Horméia للاستفادة من أحدث التحسينات.',
      action: 'تحديث',
      dismiss: 'لاحقًا',
      dismissA11y: 'إخفاء دعوة التحديث هذه',
    },
    blocking: {
      title: 'هذا الإصدار لم يعد متوافقًا',
      body: 'تطوّر Horméia منذ تثبيت هذا الإصدار، ولم يعد بإمكانه العمل بشكل صحيح مع الخدمة. هذا ليس عطلًا، ولا يوجد ما يجب إصلاحه من جهتك.',
      reassurance: 'بياناتك ومستنداتك سليمة: ستجدها فور فتح الإصدار الجديد.',
      action: 'تثبيت التحديث',
    },
  },
  quota: {
    resetDate: '1 %{monthYear}',
    capReachedNoUpgrade:
      'بلغت حد خطتك لهذه الميزة. يتجدد رصيدك في %{date}.',
    notIncludedNoUpgrade: 'هذه الميزة غير مشمولة في ترخيصك.',
  },
  errors: {
    network: 'تعذّر الاتصال. تحقق من اتصالك، ثم أعد المحاولة.',
    server: 'الخدمة غير متاحة مؤقتًا. أعد المحاولة بعد لحظات.',
    aiBadOutput: 'تعذّر استخدام استجابة الذكاء الاصطناعي. أعد التحليل.',
    profilTropEloigne: 'هذا العرض بعيد جدًا عن سيرتك الذاتية لكتابة رسالة مقنعة.',
    invalidRequest: 'تحقق من المعلومات المُدخلة، ثم أعد المحاولة.',
    ficheTropLongue: 'الوصف الوظيفي طويل جدًا: 20 صفحة أو 10000 حرف كحد أقصى.',
    unauthorized: 'انتهت جلستك. سجّل الدخول من جديد.',
  },

  nouveautes: {
    titre: 'ما الجديد في Horméia',
    offresTitre: 'عروض التكوين بالتناوب',
    offresTexte: 'اعثر على عروض التكوين بالتناوب والعمل التي تناسب ملفك، ثم جهّز طلبك بمساعدة الذكاء الاصطناعي.',
    voirOffres: 'تصفّح العروض',
    voixTitre: 'صوت مسؤول التوظيف',
    voixTexte: 'أصبح لمحاكاة المقابلات الآن صوت واقعي. حسب باقتك: في Free مقابلة واحدة شهريًا، وفي Accélérateur أو École مقابلتان أسبوعيًا، وفي Pro خمس مقابلات أسبوعيًا.',
    languesTitre: 'لغات جديدة',
    languesTexte: 'أصبح Horméia متاحًا الآن بالإسبانية والبرتغالية والصينية والعربية. يمكنك تغيير اللغة من الإعدادات.',
    politiqueTitre: 'سياسة الخصوصية',
    politiqueTexte: 'حدّثنا سياسة الخصوصية لتوضيح كيفية قياس استخدام التطبيق. يمكنك الاطلاع عليها في أي وقت من الإعدادات.',
    voirPolitique: 'اقرأ السياسة',
    bouton: 'لنبدأ',
  },
};
